import {useEffect, useState} from "react";
import {getDownloadURL, ref as storageRef} from "firebase/storage";
import FirebaseQuery from "./firebaseQuery";
import {storage} from "./firebaseConfig";
import {ScimosArticle} from "./types";
import {SectionType, Subject} from "./constants";

export type ArticleWithMeta = ScimosArticle & {url?: string};

const isDirectSrc = (src: string) =>
    /^(https?:)?\/\//.test(src) || src.startsWith("data:") || src.startsWith("blob:");

// Resolves an article's icon. `image` is either a direct URL or a Firebase
// Storage path (see ScimosArticle); returns undefined if there's no image or
// it fails to resolve.
const resolveIcon = async (image: string | null): Promise<string | undefined> => {
    if (!image) return undefined;
    if (isDirectSrc(image)) return image;
    return getDownloadURL(storageRef(storage, image)).catch(() => undefined);
};

// Fetches articles of a given section type (and optionally a single subject)
// from that type's own collection ("learn" / "news" — book reviews aren't
// browsable this way), attaches each icon URL, and returns them sorted by
// title (the schema tools/scimos_convert.py produces has no publish date).
// Returns undefined while loading. Passing an undefined type skips the query
// (useful when the type is not known until a parent document has loaded).
export function useArticles(type?: SectionType, subject?: Subject) {
    const [articles, setArticles] = useState<ArticleWithMeta[]>();

    useEffect(() => {
        if (!type) return;
        let active = true;
        const firebaseQuery = new FirebaseQuery();

        firebaseQuery.getDocsWhere([type]).then(async (result) => {
            const entries = Object.values(result as {[key: string]: ScimosArticle});
            const filtered = subject ? entries.filter((a) => a.subject.toLowerCase() === subject) : entries;
            const withUrls = await Promise.all(filtered.map(async (article) => ({
                ...article,
                url: await resolveIcon(article.image),
            })));
            withUrls.sort((a, b) => a.title.localeCompare(b.title));
            if (active) setArticles(withUrls);
        });

        return () => {
            active = false;
        };
    }, [type, subject]);

    return articles;
}
